import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Image, Text, useStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'
import {domToReact, Element, htmlToDOM, Text as HtmlText} from 'html-react-parser'
import xss from 'xss'
import CurrentPrice from '../../display-price/current-price'
import AddToCartButton from '../../add-to-cart-button'
import {CloudIcon, SummaryCheckIcon} from '../../icons'

const getSummaryItems = (summary = '') => {
    let currentNodes = []
    const hasContent = (node) =>
        node.type === 'text' ? Boolean(node.data.trim()) : (node.children || []).some(hasContent)
    const append = (node, wrappers) => {
        currentNodes.push(
            wrappers.reduceRight(
                (child, wrapper) => new Element(wrapper.name, wrapper.attribs, [child]),
                node,
            ),
        )
    }
    const appendSeparator = (wrappers) => {
        const lastNode = currentNodes[currentNodes.length - 1]
        if (lastNode?.type === 'text' && /\s$/.test(lastNode.data)) return
        append(new HtmlText(' '), wrappers)
    }
    const visit = (nodes, wrappers = []) => {
        nodes.forEach((node) => {
            if (node.name === 'br') {
                appendSeparator(wrappers)
            } else if (['p', 'ul', 'ol', 'li'].includes(node.name)) {
                appendSeparator(wrappers)
                visit(node.children || [], wrappers)
                appendSeparator(wrappers)
            } else if (node.type === 'text') {
                node.data.split(/\r\n|\r|\n/).forEach((line, index) => {
                    if (index > 0) appendSeparator(wrappers)
                    if (line) append(new HtmlText(line), wrappers)
                })
            } else if (node.children) {
                visit(node.children, [...wrappers, node])
            } else {
                append(node, wrappers)
            }
        })
    }
    visit(
        htmlToDOM(
            xss(summary, {
                whiteList: {
                    br: [],
                    p: [],
                    ul: [],
                    ol: [],
                    li: [],
                    strong: [],
                    em: [],
                    b: [],
                    i: [],
                },
                stripIgnoreTag: true,
                stripIgnoreTagBody: ['script', 'style', 'textarea', 'option'],
            }),
        ),
    )
    return hasContent({children: currentNodes}) ? [domToReact(currentNodes)] : []
}

const getSubscriptionPrice = (value) => {
    if (!value) return 0
    try {
        const prices = typeof value === 'string' ? JSON.parse(value) : value
        if (typeof prices !== 'object' || prices === null) return 0
        const entries = Object.values(prices)
        return entries.reduce(
            (sum, entry) =>
                sum + parseFloat(entry?.price || '0') * parseInt(entry?.quantity || '0', 10),
            0,
        )
    } catch {
        return 0
    }
}

const PricingCard = ({product, timing = '', isBestSeller = false}) => {
    const styles = useStyleConfig('PricingSection')
    const {formatMessage, formatNumber} = useIntl()
    const {currency} = useCurrency()
    const navigate = useNavigation()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const [error, setError] = useState(null)
    const [isAdded, setIsAdded] = useState(false)
    const errorContainerRef = useRef(null)
    const image = product.imageGroups?.find((group) => group.viewType === 'large')?.images?.[0]
    const price = getSubscriptionPrice(product.c_pricesFirst)
    const timingName = timing.trim().replace(/^\/+/, '').toLowerCase()
    const periodUnit = {monthly: 'month', annual: 'year', annually: 'year'}[timingName]
    const frequency = Number(product.c_subFrequency)
    const periodLabel =
        periodUnit && Number.isFinite(frequency) && frequency > 0
            ? `/ ${formatNumber(frequency, {style: 'unit', unit: periodUnit, unitDisplay: 'long'})}`
            : `/ ${[product.c_subFrequency, timing.trim().replace(/^\/+/, '')]
                  .filter(Boolean)
                  .join(' ')}`
    const isSubscription = product.c_isSubscription === true || product.c_isSubscription === 'true'
    const subscriptionItems = product.c_productSetProducts || []
    const childProductIds = subscriptionItems.map((item) => item.productId).filter(Boolean)
    const {
        data: childProducts,
        isLoading: isLoadingChildren,
        isError: isChildProductsError,
    } = useProducts(
        {
            parameters: {
                ids: childProductIds.join(','),
                expand: ['availability', 'images'],
            },
        },
        {enabled: childProductIds.length > 0},
    )
    const isChildOutOfStock = subscriptionItems.some((item) => {
        const child = childProducts?.data?.find((entry) => entry.id === item.productId)
        return (
            child &&
            (child.inventory?.orderable === false ||
                (child.inventory?.stockLevel || 0) < item.quantity)
        )
    })
    const isOutOfStock =
        product.inventory?.orderable === false ||
        product.inventory?.stockLevel === 0 ||
        isChildOutOfStock
    const isCheckingStock = childProductIds.length > 0 && Boolean(isLoadingChildren)
    const hasChildProductsError = childProductIds.length > 0 && Boolean(isChildProductsError)
    const handleAdded = (itemsAdded) => {
        if (!itemsAdded?.length) return
        setIsAdded(true)
        navigate('/cart')
    }
    const productName = product.c_variantIdentifier || product.name || product.productName
    const summaryItems = getSummaryItems(product.shortDescription)
    const costPerCoffee = product.c_costPerCoffee
        ? parseFloat(String(product.c_costPerCoffee).replace(',', '.'))
        : undefined
    const subscriptionDetails = [
        {
            icon: CloudIcon,
            title:
                product.c_subDuration &&
                formatMessage(
                    {
                        id: 'pricing_section.duration_months',
                        defaultMessage: 'Duration: {value} months',
                    },
                    {value: product.c_subDuration},
                ),
        },
        {
            icon: CloudIcon,
            title:
                Number.isFinite(costPerCoffee) &&
                formatMessage(
                    {
                        id: 'pricing_section.cost_per_coffee',
                        defaultMessage: 'Cost per coffee: {value}',
                    },
                    {
                        value: (
                            <CurrentPrice
                                price={costPerCoffee}
                                currency={product.currency || currency}
                                as="span"
                                {...styles.details}
                            />
                        ),
                    },
                ),
        },
        {
            icon: CloudIcon,
            title:
                product.c_coffeePerDay &&
                formatMessage(
                    {
                        id: 'pricing_section.coffee_per_day',
                        defaultMessage: 'Coffees per day: {value}',
                    },
                    {value: product.c_coffeePerDay},
                ),
        },
        {
            icon: CloudIcon,
            title:
                product.c_subDiscountPercentage &&
                formatMessage(
                    {id: 'pricing_section.discount', defaultMessage: 'Discount: {value}%'},
                    {value: product.c_subDiscountPercentage},
                ),
        },
    ].filter((detail) => detail.title)

    return (
        <Flex
            as="article"
            {...styles.card}
            {...(isBestSeller ? styles.cardHighlighted : {})}
            data-testid="pricing-card"
            data-best-seller={isBestSeller}
        >
            {isBestSeller && (
                <Text {...styles.ribbon}>
                    {formatMessage({
                        id: 'subscription.best-seller',
                        defaultMessage: 'Best Seller',
                    })}
                </Text>
            )}
            {image && (
                <Box {...styles.media}>
                    <Image
                        src={image.disBaseLink || image.link}
                        alt={image.alt || product.name || ''}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                        loading="lazy"
                    />
                </Box>
            )}
            <Box {...styles.content}>
                <Box {...styles.nameAndPriceContainer}>
                    <Text as="h3" {...styles.title}>
                        {productName}
                    </Text>
                    {product.c_pricesFirst && (
                        <Flex {...styles.priceRow} {...styles.price}>
                            <CurrentPrice
                                price={price}
                                currency={product.currency || currency}
                                {...styles.price}
                            />
                            <Text {...styles.timing}>{periodLabel}</Text>
                        </Flex>
                    )}
                </Box>
                {summaryItems.length > 0 && (
                    <Flex as="ul" {...styles.summaryList} data-testid="pricing-summary">
                        {summaryItems.map((item, index) => (
                            <Flex as="li" key={index} {...styles.summaryItem}>
                                <SummaryCheckIcon {...styles.summaryIcon} aria-hidden="true" />
                                <Box {...styles.summaryText}>{item}</Box>
                            </Flex>
                        ))}
                    </Flex>
                )}
                {subscriptionDetails.length > 0 && (
                    <Flex
                        as="ul"
                        {...styles.keyFeatures}
                        data-testid="pricing-subscription-details"
                    >
                        {subscriptionDetails.map((detail, index) => {
                            const DetailIcon = detail.icon
                            return (
                                <Flex as="li" key={index} {...styles.keyFeature}>
                                    <DetailIcon {...styles.keyFeatureIcon} aria-hidden="true" />
                                    <Text as="span">{detail.title}</Text>
                                </Flex>
                            )
                        })}
                    </Flex>
                )}
                <Box
                    ref={errorContainerRef}
                    role={error ? 'alert' : undefined}
                    display={error ? 'block' : 'none'}
                >
                    {error && (
                        <Text color="foreground.negative.default">
                            {formatMessage({
                                id: 'pricing_section.cart_error',
                                defaultMessage:
                                    'This subscription could not be added. Please try again.',
                            })}
                        </Text>
                    )}
                </Box>
                {!notShoppableCountry && (
                    <Box mt="auto">
                        {isAdded ? (
                            <Button
                                {...styles.cta}
                                variant={
                                    isBestSeller ? 'primary-not-rounded' : 'outline-not-rounded'
                                }
                                isDisabled
                                leftIcon={
                                    <SummaryCheckIcon {...styles.summaryIcon} aria-hidden="true" />
                                }
                            >
                                {formatMessage({
                                    id: 'add_to_cart_modal.label.add-to-cart-added',
                                    defaultMessage: 'Added',
                                })}
                            </Button>
                        ) : isOutOfStock ? (
                            <Button
                                {...styles.cta}
                                variant={
                                    isBestSeller ? 'primary-not-rounded' : 'outline-not-rounded'
                                }
                                isDisabled
                            >
                                {formatMessage({
                                    id: 'use_product.message.out_of_stock',
                                    defaultMessage: 'Out of stock',
                                })}
                            </Button>
                        ) : (
                            <AddToCartButton
                                product={{...product}}
                                isSubscription={isSubscription}
                                setShowErrorMessage={setError}
                                errorContainerRef={errorContainerRef}
                                showUniversalFeedback={false}
                                universalFeedback={false}
                                callback={handleAdded}
                                buttonProps={{
                                    ...styles.cta,
                                    variant: isBestSeller
                                        ? 'primary-not-rounded'
                                        : 'outline-not-rounded',
                                    isDisabled: isCheckingStock || hasChildProductsError,
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Flex>
    )
}

PricingCard.propTypes = {
    product: PropTypes.object.isRequired,
    timing: PropTypes.string,
    isBestSeller: PropTypes.bool,
}

export default PricingCard
